/**
 * Pairing: download, run, approve (ADR 0042 decision 1; R-EN-1, R-ON-3, R-SC-2).
 *
 * The node makes its key, creates an unclaimed pairing request over HTTPS, opens the system
 * browser at the approval page with a one-time secret `k` in the URL fragment, and polls until a
 * signed-in person approves or denies. No join code, no token on a command line or in a file.
 *
 * Wire rules (R-EN-2..4): HTTPS on 443 only, plain JSON request/response plus long polling, no
 * WebSocket and no LAN discovery during pairing. Every node call is signed by the node key; the
 * server never sees `k`, only `kHash`.
 *
 *   Node (no session, rate-limited per source IP)
 *     POST   /api/pair/requests                    create        PairCreateRequest  -> PairCreated
 *     GET    /api/pair/requests/:id/poll?wait=25    poll (signed) -> PairPoll
 *     DELETE /api/pair/requests/:id                 cancel (signed)  -> 200 PairPoll, state 'cancelled'
 *     (cancel answers 200 with the request as a PairPoll in state 'cancelled', again for a request
 *     already cancelled; one that ended another way is refused: 409 already-decided or conflict,
 *     410 expired)
 *   Console (signed-in person)
 *     GET    /api/pair/requests/:id                 view          -> PairRequestView
 *     POST   /api/pair/requests/:id/claim           { k } or { words }  -> PairRequestView
 *     POST   /api/pair/requests/:id/approve         PairApproveRequest  -> PairRequestView
 *     POST   /api/pair/requests/:id/deny            PairDenyRequest     -> PairRequestView
 *     GET    /api/pair/requests?state=claimed       my requests         -> PairRequestView[]
 *     An unclaimed request is answered as 404 to anyone but a loopback console for a loopback
 *     request; opening it takes `k` or the typed words.
 *
 * Node calls carry the header `Orgus-Pair-Proof: <ts>.<sig>`: base64url signature by the node key
 * over pairPollSigningInput(requestId, ts) (or pairCancelSigningInput), ts in ms within
 * PAIR_PROOF_SKEW_MS of server time.
 *
 * Encodings, exactly (both servers and the Go agent; settled with the Go client, 18 Oct 2026).
 * Every pairing encoding is unpadded base64url:
 *   k            16 random bytes; 22 characters in `#k=` and in the claim body. The server decodes
 *                it to bytes, hashes them and compares with kHash in constant time
 *   kHash        SHA-256 over the 16 raw bytes of k (not over its text)
 *   key          the node's SPKI DER, P-256 (store any) or Ed25519 (store 'file')
 *   sig, proofs  the create sig and the Orgus-Pair-Proof signature. P-256: raw r||s, 64 bytes,
 *                over SHA-256 of the UTF-8 signing input (WebCrypto's form; high or low S both
 *                verify). Ed25519: 64 bytes
 *   hubKeys      the hub command key's SPKI DER (the P0 challenge carries the same key in
 *                standard base64; decode both to bytes to compare)
 *   serverSig    Ed25519 by hubKeys[0] over pairGrantSigningInput (serverSigAlg 'ed25519', which
 *                is inside the signed grant; a missing serverSigAlg means ed25519)
 *
 * Clock: PairCreated, PairPoll and every pairing error body carry `serverTime` (ms), and every
 * response has a standard Date header. A signed call more than PAIR_PROOF_SKEW_MS off is refused
 * with 'stale-proof' and serverTime; only that call fails, never the request: the node retries
 * with its clock offset applied.
 *
 * Node calls (security fixes, 18 Oct 2026): they never carry an Origin header (a web page's
 * request is refused, 403 not-allowed) and a POST body is `Content-Type: application/json`
 * (anything else is 415). kHash and key.publicKey must be the canonical unpadded base64url of
 * their bytes (a re-spelled one is refused, 400). One long poll per request at a time: a newer
 * poll answers the older one at once. A request replaced by the same key with a new code is
 * deleted, not kept as cancelled: its poll answers 404, superseded or expired (the request is
 * gone; start a new one).
 *
 * Transport (review P3, 18 Oct 2026): a hub refuses every pairing call from another computer
 * (create, poll, cancel, and the approval page's peek) unless it arrived over TLS, on the socket
 * or through a proxy the hub trusts that says https: 403 not-allowed, "This hub pairs other
 * computers only over HTTPS." The control plane refuses a plain-HTTP call too (a loopback host
 * aside, for local development). A node should never pair over plain HTTP to another computer.
 *
 * After approval the node connects to `grant.connectUrl` through the normal P0 handshake with
 * no secret and no join token: hello carries `machine.id = grant.machineId` and a v2 NodeProof
 * with `alg: 'p256'` (or 'ed25519'), the same public key, the challenge's hub key fingerprint
 * (which must match fingerprintOf(hubKeys[0])) and a connection counter that rises from 1.
 */
import { canonicalJson } from './trust.ts';
import type { PairTicketOutcome } from './pairing-tickets.ts';

export const PAIRING_EXT = 'pairing';
export const PAIR_API = '/api/pair/requests';
/** The browser page the node opens: `${origin}${PAIR_PAGE}/${requestId}#k=${k}`. */
export const PAIR_PAGE = '/pair';
/** A request that is not approved within this long expires. */
export const PAIR_TTL_MS = 10 * 60_000;
/** Longest long-poll the server holds open (seconds, `?wait=`). */
export const PAIR_POLL_MAX_WAIT_S = 25;
/** Clock skew allowed on a node's signed call. */
export const PAIR_PROOF_SKEW_MS = 60_000;
/** Header carrying the node's signature on poll and cancel. */
export const PAIR_PROOF_HEADER = 'Orgus-Pair-Proof';
/**
 * Caps the server applies before anyone signs in. openPerIp counts per network (an IPv4 address,
 * an IPv6 /64) and openPerPrefix per block (IPv4 /24, IPv6 /48); createsPerMinute caps every
 * create (accepted or not) per network and per node key, and createsPerBlockPerMinute per block,
 * so rotating through the /64s of one /48 (or the addresses of one /24) buys nothing; except one
 * refused because the node's clock is off (stale-proof): those count only toward stalePerMinute
 * per network, so a node that fixes its clock and retries uses one create, not two. wordTries
 * wrong words from one person decline a Flow C request (review P6: from someone who may claim
 * it, operator or above; other people's wrong words never decline it); wordTriesTotal wrong
 * words from anyone stop words being taken for it (429, nothing declined). (openPerPrefix,
 * createsPerMinute and wordTries are additive, 18 Oct 2026; stalePerMinute too, later the same
 * day; createsPerBlockPerMinute and wordTriesTotal with the review follow-ups, 18 Oct 2026.)
 *
 * When an open-request cap (openPerIp, openPerPrefix, openTotal) is full, the server evicts the
 * oldest unclaimed request that cap counts (never a claimed one) and takes the new one: the
 * evicted request's poll answers 404, superseded or expired (the request is gone; start a new
 * one), which a node treats as expired. Nothing is
 * audited for it (an unclaimed request belongs to no org; the server log says so). Only when
 * every request the cap counts is claimed is the create refused: that 429 carries Retry-After,
 * the seconds until the oldest of them expires, 30 to 600. Any other pairing 429 carries
 * Retry-After: 60.
 */
export const PAIR_LIMITS = {
  openPerIp: 5,
  openPerPrefix: 20,
  openTotal: 500,
  createsPerMinute: 10,
  createsPerBlockPerMinute: 30,
  stalePerMinute: 30,
  wordTries: 5,
  wordTriesTotal: 20,
  stringMax: 128,
  harnessesMax: 8,
} as const;

export const PAIR_CREATE_CONTEXT = 'orgus-pair-create-v1';
export const PAIR_POLL_CONTEXT = 'orgus-pair-poll-v1';
export const PAIR_CANCEL_CONTEXT = 'orgus-pair-cancel-v1';

/**
 * unclaimed  created by the node; nobody has opened it yet
 * claimed    a signed-in person opened it with the right `k` (or picked the right words)
 * approved   approved: the node may connect with its key
 * denied     a person said no, or picked the wrong words
 * expired    nobody approved within PAIR_TTL_MS
 * conflict   a second key presented the same claim: both requests cancelled, alert raised
 * cancelled  the node cancelled (user closed the installer, or it restarted with a new request)
 */
export type PairState = 'unclaimed' | 'claimed' | 'approved' | 'denied' | 'expired' | 'conflict' | 'cancelled';
export const PAIR_FINAL_STATES: readonly PairState[] = ['approved', 'denied', 'expired', 'conflict', 'cancelled'];

/** How the person approves. 'browser': this computer's browser with `#k` (Flow A). 'words': another device picks the three words (Flow C). */
export type PairFlow = 'browser' | 'words';

export type PairOs = 'windows' | 'darwin' | 'linux';
export type PairArch = 'amd64' | 'arm64';
/** Where the node keeps its key (ADR 0042 decision 2). */
export type PairKeyStore = 'tpm' | 'keystore' | 'keychain' | 'secret-service' | 'file';

export interface PairNodeKey {
  /** 'p256': ECDSA P-256, raw r||s low-S (ADR 0042). 'ed25519' only from v1 agents. */
  alg: 'p256' | 'ed25519';
  /** base64url SPKI DER. */
  publicKey: string;
  store: PairKeyStore;
}

/** Facts the node reports about itself. Shown as "reported by the computer". */
export interface PairMachine {
  /**
   * The name the installer was asked to show (install --name), cleaned; absent when none. A join
   * ticket names the machine with it and the approval page offers it; the approver may change it.
   */
  name?: string;
  hostname: string;
  os: PairOs;
  osVersion: string;
  arch: PairArch;
  agentVersion: string;
  /** The OS account the node runs as (user mode). */
  user: string;
}

export interface PairHarness {
  id: string; // 'claude-code' | 'codex' | ...
  version: string | null;
  /** Signed in to its provider; null when the node cannot tell. */
  signedIn: boolean | null;
}

/** POST /api/pair/requests (node). */
export interface PairCreateRequest {
  v: 1;
  flow: PairFlow;
  key: PairNodeKey;
  /** base64url SHA-256 of the one-time 128-bit secret `k` (Flow A). Absent for Flow C. */
  kHash?: string;
  /**
   * A join ticket (additive, ADR 0090 decision 2): the secret from a `/join/<ticket>` link, read
   * from the clipboard, `--ticket-file` or stdin; never logged. Flow A only, beside kHash: a
   * refused ticket leaves the request unclaimed for the browser page. Covered by `sig`.
   */
  ticket?: string;
  machine: PairMachine;
  harnesses: PairHarness[];
  /** Node clock, ms. */
  ts: number;
  /** base64url node signature over pairCreateSigningInput(this without sig). */
  sig: string;
}

/** 201 response to create. */
export interface PairCreated {
  requestId: string;
  state: 'unclaimed';
  expiresAt: number;
  /** Open this in the system browser, appending `#k=<k>` for Flow A. */
  approveUrl: string;
  /** Human-checkable fingerprint of the node key, formatPairFingerprint(sha256 of SPKI). */
  fingerprint: string;
  /** Flow C only: the three words the node shows. */
  words?: [string, string, string];
  /** Suggested delay before the first poll. */
  pollAfterMs: number;
  /** The server's clock (ms), so the node can correct its own for later proofs. */
  serverTime: number;
  /** The request carried a ticket (additive, ADR 0090): redeemed (poll now, no browser) or refused (Flow A). */
  ticket?: PairTicketOutcome;
}

/** What the node gets once approved. Everything it needs to connect with its key; no secret. */
export interface PairGrant {
  machineId: string;
  /** The hub/gateway command keys the node pins (unpadded base64url SPKI DER), newest first. */
  hubKeys: string[];
  /** wss URL the node connects to on 443. */
  connectUrl: string;
  org: { id: string; name: string; domain: string | null };
  approvedBy: { memberId: string; name: string; email: string | null };
  scope: 'personal' | 'team';
  /** Auto-update is on by default once paired (R-EN-1); the approver may turn it off. */
  autoUpdate: { enabled: boolean; channel: 'stable' | 'beta' };
  /** How serverSig is made; inside the signed grant. Absent means 'ed25519'. */
  serverSigAlg?: 'ed25519' | 'p256';
  /**
   * What the signed grant is bound to (additive, review P3, 18 Oct 2026): the request it answers,
   * the node key that made it (unpadded base64url SPKI, exactly as in the create request) and the
   * server time it was issued (ms). A node refuses a grant whose requestId is not its request,
   * whose nodePublicKey is not its own key, or whose issuedAt is outside its request's lifetime
   * (expiresAt - PAIR_TTL_MS to expiresAt, with a minute of slack either side). A server older
   * than that sends none of them; a node checks each one that is present.
   */
  requestId?: string;
  nodePublicKey?: string;
  issuedAt?: number;
  /** Server signature over canonicalJson(grant without serverSig), by hubKeys[0]. */
  serverSig: string;
}

/** GET /api/pair/requests/:id/poll (node). Final states end polling. */
export interface PairPoll {
  requestId: string;
  state: PairState;
  expiresAt: number;
  /** One sentence the node shows: what is happening and what to do next (R-ON-6). */
  message: string;
  /** Present only when state is 'approved'. */
  grant?: PairGrant;
  /** When not final, poll again after this long. */
  pollAfterMs?: number;
  /** The server's clock (ms). */
  serverTime: number;
}

/** GET /api/pair/requests/:id (console). */
export interface PairRequestView {
  requestId: string;
  state: PairState;
  flow: PairFlow;
  createdAt: number;
  expiresAt: number;
  fingerprint: string;
  /** Reported by the computer: strings are length-capped and stripped of control and bidi characters. */
  machine: PairMachine;
  harnesses: PairHarness[];
  /** The request came from a different public network than this browser. */
  differentNetwork: boolean;
  claimedBy?: { memberId: string; name: string };
  decidedBy?: { memberId: string; name: string; at: number };
  /**
   * No longer sent (18 Oct 2026, security fix C3): the person types the words the computer
   * shows instead of picking from choices the server gave to anyone who knew the request id.
   */
  wordChoices?: [string, string, string][];
  /** The org the computer joins on approval (additive, for the "Add this computer to <org>?" card). */
  org?: { id: string; name: string };
  /** Approved: the machine it became (additive, for the link to it). */
  machineId?: string;
  /** Approved: the name it was given (additive). */
  machineName?: string;
  /** The person looking claimed it (additive). */
  mine?: boolean;
  /** The person looking may approve or decline it (additive): an admin (R-SC-2, ORGU-30). */
  canDecide?: boolean;
  /**
   * The person looking opened it and is not an admin (additive, ORGU-30): it waits for an admin
   * to approve it, into this person's personal scope.
   */
  awaitingAdmin?: boolean;
  /** The full SHA-256 of the node key's SPKI, lowercase hex (additive: "Show full fingerprint"). */
  fingerprintHex?: string;
  /**
   * Who the computer is being added to (additive, WP-14): the server's domain as the computer
   * reached it, and the hub key the computer will pin, as formatPairFingerprint of its SHA-256.
   */
  server?: { domain: string; keyFingerprint: string };
  /**
   * The node key was seen before (additive, LEAVE-AUDIT-3): bound to a machine that is still
   * live (approving replaces it), or to one that was removed (by an admin: only an admin may
   * approve it again; by the node itself on uninstall: anyone may).
   */
  keyHistory?: { state: 'live' | 'removed'; machineId: string; machineName: string | null; removedBy: 'admin' | 'node' | null; at: number | null };
  /** Approved by a join ticket the claimer made (additive, ADR 0090): its id. */
  ticketId?: string;
}

/** POST /api/pair/requests/:id/claim (console). Exactly one of k or words. */
export interface PairClaimRequest {
  /** Flow A: the `k` from the URL fragment, base64url. */
  k?: string;
  /**
   * Flow C: the three words the person typed, in order (any case). A wrong try changes nothing
   * but a counter; PAIR_LIMITS.wordTries of them from one person decline the request with an
   * alert, and PAIR_LIMITS.wordTriesTotal from anyone stop words being taken for it. (null is
   * no longer accepted: there are no choices to reject.)
   */
  words?: [string, string, string] | null;
  /**
   * Flow A (additive, review I2, 18 Oct 2026): the person confirmed "I started the installer on
   * this computer". Required when the request came from a different network than this browser
   * (PairRequestView.differentNetwork); without it the claim is refused (409 not-allowed) and
   * the code stays usable. Audited with the claim.
   */
  differentNetworkAck?: boolean;
}

/** POST /api/pair/requests/:id/approve (console). */
export interface PairApproveRequest {
  /** Defaults to the reported hostname. */
  name?: string;
  scope?: 'personal' | 'team';
  tags?: string[];
  /** Default true (R-EN-1). */
  autoUpdate?: boolean;
  /** WebAuthn assertion over the pairing intent, when a passkey is available (ADR 0042 decision 5). */
  passkey?: { credentialId: string; clientDataJSON: string; authenticatorData: string; signature: string };
}

/** POST /api/pair/requests/:id/deny (console). */
export interface PairDenyRequest {
  reason?: string;
}

/** Error codes in `{ error: { code, what, why, next } }` on any pairing route. */
export type PairErrorCode =
  | 'not-found'
  | 'expired'
  | 'already-decided'
  | 'conflict'
  | 'bad-proof'
  | 'stale-proof'
  | 'bad-k'
  | 'wrong-words'
  | 'rate-limited'
  | 'not-allowed'
  | 'unsupported-version';

/** The body of every pairing error: what failed, why, what next, the code, and the server's clock. */
export interface PairErrorBody {
  error: { what: string; why: string; next: string; code: PairErrorCode };
  serverTime: number;
}

/** The sentence the node shows for each state (R-ON-6: what is happening, what next). */
export const PAIR_STATE_MESSAGE: Record<PairState, string> = {
  unclaimed: 'Waiting for you to approve this computer in the browser window that just opened.',
  claimed: 'Almost there. Approve this computer on the page in your browser.',
  approved: 'Paired. This computer is now connected to Orgus.',
  denied: 'The request was declined. Run the installer again if this was a mistake.',
  expired: 'The request timed out after 10 minutes. Start pairing again.',
  conflict: 'Another computer tried to use the same approval, so both were stopped for safety. Start pairing again.',
  cancelled: 'Pairing was cancelled on this computer.',
};

/** What the node shows while a request a member opened waits for an admin (ORGU-30). */
export const PAIR_AWAITING_ADMIN_MESSAGE = 'Almost there. An admin approves new computers: this one joins as soon as they do.';

/**
 * Audit actions pairing writes, one per state change (R-SC-2). A wrong-words pick is a
 * 'pairing.denied' with result 'error' and params.reason 'wrong-words'; a conflict and a
 * wrong-words pick raise a security alert.
 */
export const PAIR_AUDIT_LABEL: Record<string, string> = {
  'pairing.requested': 'Pairing requested',
  'pairing.claimed': 'Pairing request opened',
  'pairing.approved': 'Computer approved',
  'pairing.denied': 'Pairing request declined',
  'pairing.expired': 'Pairing request expired',
  'pairing.conflict': 'Pairing request stopped: conflict',
  'pairing.cancelled': 'Pairing request cancelled',
  /** Work refused on a personal computer that is not the caller's (ADR 0042 addendum, 18 Oct 2026). */
  'pairing.refused': 'Work refused: personal computer',
  /** Join links (ADR 0090): the redeem itself is pairing.claimed and pairing.approved with params.via 'ticket'. */
  'pairing.ticket-minted': 'Join link made',
  'pairing.ticket-cancelled': 'Join link cancelled',
  'pairing.ticket-refused': 'Join link refused',
};

/**
 * Flow C words: the server picks three for the node to show, and two decoy triples. 256 short,
 * distinct, common English words (8 bits each), so a person reads them out without spelling.
 */
export const PAIR_WORDS: readonly string[] = (
  'acorn actor adult agent alarm album alert alley amber angle ankle apple april arena armor arrow ' +
  'atlas attic audio aunt award bacon badge bagel baker bamboo banjo barn basil beach beard beaver ' +
  'bench berry bike bingo birch bison blade blanket bloom board bonus boot bottle bowl brain branch ' +
  'brave bread brick bridge brook brush bucket buffalo bunny butter cabin cable cactus camel camera ' +
  'canal candle canoe canvas canyon carbon carpet carrot castle cedar cello chair chalk cheese cherry ' +
  'chess chief chimney cider circle citrus clay cliff clock cloud clover coach cobalt cocoa coconut ' +
  'comet copper coral cotton cougar cousin crane crater cricket crystal cupboard dancer daisy delta ' +
  'desert diamond dinner dolphin donkey dragon drum eagle easel echo elbow ember engine falcon fern ' +
  'ferry fiddle finch flame flute forest fossil fountain fox galaxy garden garlic gecko ginger glacier ' +
  'globe goose granite grape gravel guitar hammer harbor harvest hazel helmet heron hollow honey hornet ' +
  'island ivory jacket jaguar jasmine jelly jungle kettle kiwi koala ladder lagoon lantern lemon ' +
  'lettuce lilac lime lizard lobster locket lotus magnet mango maple marble meadow melon meteor mint ' +
  'mirror mitten moose mosaic muffin museum nectar needle nickel noodle oasis ocean olive onion orbit ' +
  'orchid otter oyster paddle panda parrot pebble pelican pepper piano pickle pigeon pillow pine ' +
  'planet plum pocket pond potato prairie puffin pumpkin quartz quill rabbit radar raven ribbon river ' +
  'robin rocket saddle salmon sandal satin scarf shadow shell silver sketch sparrow spider spruce ' +
  'squash statue stove sugar summit sunset swan tablet teapot temple thistle thunder tiger timber ' +
  'tomato topaz tractor trumpet tulip tunnel turtle velvet violet walnut walrus whistle willow ' +
  'window winter wizard yogurt zebra zipper'
)
  .split(' ')
  .slice(0, 256);

export function isPairState(v: unknown): v is PairState {
  return typeof v === 'string' && v in PAIR_STATE_MESSAGE;
}

export function isPairFinal(s: PairState): boolean {
  return PAIR_FINAL_STATES.includes(s);
}

/** The exact text the node signs to create a request. */
export function pairCreateSigningInput(req: Omit<PairCreateRequest, 'sig'>): string {
  return `${PAIR_CREATE_CONTEXT}\n${canonicalJson(req)}`;
}

/** The exact text the node signs to poll. */
export function pairPollSigningInput(requestId: string, ts: number): string {
  return `${PAIR_POLL_CONTEXT}\n${requestId}\n${ts}`;
}

/** The exact text the node signs to cancel. */
export function pairCancelSigningInput(requestId: string, ts: number): string {
  return `${PAIR_CANCEL_CONTEXT}\n${requestId}\n${ts}`;
}

/** The exact text the server signs on a grant. */
export function pairGrantSigningInput(grant: Omit<PairGrant, 'serverSig'>): string {
  return `orgus-pair-grant-v1\n${canonicalJson(grant)}`;
}

const B32 = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * A fingerprint a person can compare at a glance: the first 80 bits of the SHA-256 of the node's
 * SPKI, as 16 characters from an alphabet without 0/O or 1/I, in four groups ("K7Q2-9XMA-...").
 * Pass the hash as lowercase hex (64 characters); both ends compute the hash themselves.
 */
export function formatPairFingerprint(sha256Hex: string): string {
  if (!/^[0-9a-f]{64}$/.test(sha256Hex)) throw new Error('fingerprint needs a 64-character lowercase hex SHA-256');
  let bits = '';
  for (let i = 0; i < 20; i++) bits += parseInt(sha256Hex[i]!, 16).toString(2).padStart(4, '0');
  let out = '';
  for (let i = 0; i < 16; i++) out += B32[parseInt(bits.slice(i * 5, i * 5 + 5), 2)]!;
  return out.match(/.{4}/g)!.join('-');
}

/** Strip control and bidirectional characters and cap the length of a string a node reports. */
export function cleanReported(s: unknown, max: number = PAIR_LIMITS.stringMax): string {
  if (typeof s !== 'string') return '';
  // C0/C1 controls, and bidi embeddings, overrides and isolates.
  return s.replace(/[\u0000-\u001f\u007f-\u009f\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '').trim().slice(0, max);
}
